import { createContext, useReducer, useMemo, useContext, useEffect } from 'react';
import { setUser as setSentryUser } from '@sentry/nextjs';
import { STEP } from '../utils/Constants';
import { FIELD_VALIDATION_REGEX } from '../utils/Validations';
import { setAmpUser } from '../utils/Amplitude';

/**
 * @typedef {object} EnFr
 * @property {string} en
 * @property {string} fr
 */
/**
 * @typedef {object} EnFrOrKey En/Fr should take precedence over Key
 * @property {string} [En]
 * @property {string} [Fr]
 * @property {string} [Key]
 */
/**
 * @typedef {'M'
 * |'S'
 * |'S001'
 * |'RQP'
 * |'PEM'
 * |'PEY'
 * |'CRANRA'
 * |'CRABIN'
 * |'CRAPE1'
 * |'CRAPE2'
 * |'CRAPE3'
 * } CheckRoutine
 */
/**
 * @typedef {object} Validation
 * @property {string} [MinLength]
 * @property {string} [MaxLength]
 * @property {string} [ExactLength]
 * @property {string} [ValidStart]
 *  Space delimited list of valid sets of characters the value must start with.
 * @property {string} [ValidEnd]
 *  Space delimited list of valid sets of characters the value must end with.
 * @property {string} [InvalidStart]
 *  Space delimited list of valid sets of characters the value must not start with.
 * @property {string} [InvalidEnd]
 *  Space delimited list of valid sets of characters the value must not end with.
 * @property {CheckRoutine} [CheckRoutine]
 */
/**
 * @typedef {object} Field See {@link https://docs.google.com/document/d/1kLqCqz5z7Vv8u_bKryNXLyMc8TGKU1kQGCKj70Fy2xI/|documentation}
 * @property {EnFrOrKey} Name Shown as the short field name if the value entered fails validation.
 * @property {EnFrOrKey} Label Field label.
 * @property {EnFrOrKey} [HelpText] More information about the field or format of the field.
 * @property {EnFrOrKey} [Placeholder] Typically used to show the format or an example.
 * @property {string} [Value] Default value to be filled into the input field.
 * @property {string} [HelpImage]
 *  URL of an image to display when the value entered fails validation.
 * @property {Validation} [Validation]
 * @property {string} [Mask] Input mask that helps the user enter the value.
 * @property {'ClientRef'
 * |'InvoiceNo'
 * |'PARTNERFIELD01'
 * |'PARTNERFIELD02'
 * |'PARTNERFIELD03'
 * |'PARTNERFIELD04'
 * |'PARTNERFIELD05'
 * } DBField Where the value the user enters will be stored in the payment tables.
 * @property {'Text'|'Date'|'Hidden'} [Type] What field user control should be used.
 */

/**
 * @typedef {object} PSWebConfig See {@link https://docs.google.com/document/d/1kLqCqz5z7Vv8u_bKryNXLyMc8TGKU1kQGCKj70Fy2xI/|documentation}
 * @property {string} [AccountNameLabelKey] lang file key for AccountNameLabel
 * @property {EnFrOrKey} [AccountNameLabel] overrides AccountNameLabelKey
 * @property {string} [AccountNameMaxLength] the content of the string is a number
 * @property {EnFrOrKey} [BannerText]
 * @property {Field[]} Fields
 */

/**
 * @typedef {object} PaymentOption
 * @property {'Credit'|'InPerson'|'eTransfer'|'PayPal'|'PeoplesRFM'} Name
 * @property {bool} Enabled
 * @property {EnFr} Title display text of the payment option name
 * @property {EnFr} Description1 main description of the payment option
 * @property {EnFr} Description2 secondary description of the payment option
 * @property {EnFr} Delivery text for the processing time of this payment option
 * @property {string} Blurb use Blurb2 instead
 * @property {EnFr} Blurb2 description to display to the user if the payment option is disabled
 * @property {object} Fee Deprecated; use FeeV2 instead
 * @property {FeeV2} FeeV2
 * @property {object} [RecipientInfo] (only present for eTransfer)
 *   Deprecated; using hardcoded recipient info instead.
 */
/**
 * @typedef {object} FeeV2 Applicable tier needs to be chosen based on the payment amount
 * @property {number} AmountLimit not enforced on frontend
 * @property {string} ProdCode not used on frontend
 * @property {FeeTier[]} Tiers
 */
/**
 * @typedef {object} FeeTier The formula should be: Fee = Percent * PaymentAmount + Fixed
 * @property {number} Amount The minimum amount at which this tier is applied
 * @property {number} Fixed Fixed amount to be applied as fee
 * @property {number} Percent
 *   Percentage of the payment amount in decimal that should be applied as fee
 */

/**
 * @typedef {object} PartnerInfo
 * @property {number} PartnerSeq
 * @property {bool} IsCRA Whether the partner's RemittanceType starts with CRA
 * @property {bool} Active Whether the partner is active (i.e. user can make a payment)
 * @property {bool} Tokenization
 * @property {EnFr} DisplayName The display name of the partner
 * @property {string} LogoURL The URL of the partner's logo
 * @property {EnFr} LogoAlt The alt text for the partner's logo
 * @property {PSWebConfig} PSWebConfig
 * @property {PaymentOption[]} PaymentOptions
 *   The list will only contain the payment options enabled for this partner
 */

/**
 * @typedef {AlgoliaHitOtherThanLandingPageData&LandingPageData} AlgoliaHit
 */
/**
 * @typedef {object} AlgoliaHitOtherThanLandingPageData
 * @property {string} objectID PartnerSeq
 * @property {string} DisplayNameEn The English display name of the partner
 * @property {string} DisplayNameFr The French display name of the partner
 * @property {string} DisplayDescriptionEn Usually used for CRA tax descriptions
 * @property {string} DisplayDescriptionFr Usually used for CRA tax descriptions
 * @property {string} City The city where the partner is located
 * @property {string} Province The province where the partner is located
 * @property {string} RemittanceType The type of remittance for the partner
 */
/**
 * @typedef {object} LandingPageData Data from AlogliaHit that's shared for
 *   all objects with the same LandingPageKey
 * @property {string} LandingPageKey The key used for filter search pages
 * @property {string} LandingPageNameEn Payment recipient name to show on the filter search page
 * @property {string} LandingPageNameFr Payment recipient name to show on the filter search page
 * @property {string} LogoUrl The URL of the partner's logo
 * @property {string} LogoAltEn The English alt text for the partner's logo
 * @property {string} LogoAltFr The French alt text for the partner's logo
 */

/**
 * @typedef {object} AccountDetails
 * @property {string} Email Email for delivering the receipt and creating payment records.
 * @property {string} FullName AccountName
 * @property {string} ClientRef Account number / client identifier
 * @property {string} [PARTNERFIELD01] Typically: n/a
 * @property {string} [PARTNERFIELD02] Typically: Period end, any date field
 * @property {string} [PARTNERFIELD03] Typically: Gross Payroll
 * @property {string} [PARTNERFIELD04] Typically: Number of Employees
 * @property {string} [PARTNERFIELD05] Typically: n/a
 * @property {string} [InvoiceNo] Unique payment identifier
 *   (typically used with Smart Payment Links)
 * @property {string} PaymentAmount The amount the user wants to pay, excluding fee.
 *   Make sure to format the string before rendering the value
 */

/**
 * @typedef {object} ReferralFields Referral fields to be inserted with the LheCodes DB table record
 * @property {string} [Domain] LheCodes.ReferralDomain
 * @property {string} [Url] LheCodes.ReferralUrl
 * @property {string} [Program] LheCodes.ReferralProgram
 * @property {string} [Data] LheCodes.ReferralData
 */

/**
 * @typedef {object} PaymentCode Information that identifies a payment for a specific
 * payment option. Only one of the properties should be present, other than for eTransfer.
 * @property {string} [LoadhubCode] (InPerson) QR code used to pay in person at Canada Post
 * @property {string} [eTransferQuestion] (eTransfer) Security question
 * @property {string} [eTransferAnswer] (eTransfer) Answer for the security question
 * @property {string} [Ticket] Moneris ticket for credit card payments
 * @property {string} [OrderId] PayPal order ID
 * @property {string} [PayPalSeq] PayPal_Seq from PayPal DB table
 * @property {string} [PaymentUrl] PeoplesRFM payment URL
 * @property {string} [PartnerPaySeq] PartnerPay_Seq from PartnerPay DB table
 */

/**
 * @typedef {object} PaymentProcessState
 * @property {boolean} isSessionLoaded
 *   indicates whether the session state has been loaded from sessionStorage
 * @property {import('../utils/Constants').STEP} step
 * @property {LandingPageData|null} landingPageData
 * @property {AlgoliaHit|null} algoliaObject Partner that user has selected from Algolia
 * @property {PartnerInfo|null} partner Partner info from the GetPartnerInfo API
 * @property {AccountDetails|null} accountDetails All values user has entered in the
 *   Details step along with hidden field values from PSWebConfig
 * @property {boolean} isInvoice indicates whether an InvoiceNo was passed in as Smart Payment Link
 * @property {ReferralFields|null} referral
 * @property {'Intuit'|null} siteMode Stored as LheCodes.SiteMode in DB
 * @property {PaymentOption & { totalAmount: number, totalFee: number }|null} paymentOption
 *   The payment option user has selected along with the caculated total amount and fee
 * @property {PaymentCode|null} paymentCode The payment code specific to the chosen payment option
 *   that allows the user to pay the appropriate recipient
 * @property {string|null} sessionToken
 * @property {string|null} transNo
 */

/**
 * @type {React.Context<{
 *  paymentProcess: PaymentProcessState,
 *  dispatchPaymentProcess: React.Dispatch<PaymentProcessEventHandler>
 * }>}
 */
export const PaymentProcessContext = createContext();

/** @type {PaymentProcessState} */
const initialState = {
  isSessionLoaded: false,
  step: STEP.CHOOSE_PARTNER,
  landingPageData: null,
  algoliaObject: null,

  // begin payment
  partner: null,
  accountDetails: null,
  isInvoice: false,
  referral: null,
  siteMode: null,
  paymentOption: null,

  // complete payment
  paymentCode: null,
  sessionToken: null,
  transNo: null,
};
/**
 * @param {PaymentProcessState} state
 * @returns {string} stringified state, with unnecessary data removed
 */
function serializeState(state) {
  const {
    step,
    partner,
    accountDetails,
    isInvoice,
    referral,
    siteMode,
    paymentOption,
    paymentCode,
    sessionToken,
    transNo,
  } = state;
  return JSON.stringify({
    step,
    partner,
    accountDetails,
    isInvoice,
    referral,
    siteMode,
    paymentOption,
    paymentCode,
    sessionToken,
    transNo,
  });
}
/**
 * Deserializes the state from sessionStorage, ignoring any invalid values
 * @param {string} serializedState stringified state from sessionStorage
 * @returns {PaymentProcessState} deserialized state with invalid values ignored
 */
function deserializeState(serializedState) {
  /** @type {PaymentProcessState} */
  let state;
  try {
    state = JSON.parse(serializedState) || {};
  } catch {
    state = {};
  }
  state = {
    step: state.step,
    partner: state.partner,
    accountDetails: state.accountDetails,
    isInvoice: state.isInvoice,
    referral: state.referral,
    siteMode: state.siteMode,
    paymentOption: state.paymentOption,
    paymentCode: state.paymentCode,
    sessionToken: state.sessionToken,
    transNo: state.transNo,
  };

  if (
    typeof state.step !== 'number' ||
    !Object.values(STEP).includes(state.step)
  ) delete state.step;
  if (
    !state.accountDetails ||
    typeof state.accountDetails !== 'object' ||
    typeof state.accountDetails.ClientRef !== 'string' ||
    typeof state.accountDetails.FullName !== 'string' ||
    typeof state.accountDetails.Email !== 'string' ||
    typeof state.accountDetails.PaymentAmount !== 'string' ||
    !FIELD_VALIDATION_REGEX.VALID_DECIMAL.test(state.accountDetails.PaymentAmount)
  ) delete state.accountDetails;
  if (!state.isInvoice || typeof state.isInvoice !== 'boolean') delete state.isInvoice;
  if (!state.referral || typeof state.referral !== 'object') delete state.referral;
  if (state.siteMode !== 'Intuit') delete state.siteMode;
  if (
    !state.paymentOption ||
    typeof state.paymentOption !== 'object' ||
    typeof state.paymentOption.Name !== 'string' ||
    typeof state.paymentOption.totalAmount !== 'number' ||
    typeof state.paymentOption.totalFee !== 'number'
  ) delete state.paymentOption;
  if (typeof state.partner !== 'object') delete state.partner;
  if (state.paymentOption && state.partner) {
    if (!state.partner.PaymentOptions?.find((po) => po.Name === state.paymentOption.Name)) {
      delete state.partner;
      delete state.paymentOption;
    }
  }

  return state;
}

/**
 * Returns a copy of the provided Referral object with its
 * Url and Domain properties set to the provided URL.
 * @param {ReferralFields|null} referral
 * @param {string|null} referrerUrl
 * @returns {ReferralFields|null} if referrerUrl is invalid, returns the original referral object
 */
export function setReferralUrl(referral, referrerUrl) {
  try {
    const original = referral ?? {};
    return {
      ...original,
      // using hostname instead of host as it doesn't include the port number,
      // which matches ASP.NET's behaviour with HttpRequest.UrlReferrer.Host
      Domain: new URL(referrerUrl).hostname,
      Url: referrerUrl,
    };
  } catch {
    return referral;
  }
}

/**
 * Each dispatcher is a function that returns
 * the {@link PaymentProcessEventHandler} to be dispatched.
 */
export const DISPATCHERS = Object.freeze({
  LOAD_SESSION: (sessionState) => (state) => ({
    ...state,
    ...sessionState,
    isSessionLoaded: true,
  }),
  RESET_PAYMENT_PROCESS: () => () => ({
    ...initialState,
  }),
  INCREMENT_PAYMENT_STEP: () => (state) => ({
    ...state,
    step: state.step + 1,
  }),
  DECREMENT_PAYMENT_STEP: () => (state) => ({
    ...state,
    step: state.step - 1,
  }),
  /** @param {STEP} step */
  SET_PAYMENT_STEP: (step) => (state) => ({
    ...state,
    step,
  }),
  /** @return {(state: PaymentProcessState) => PaymentProcessState} */
  LOAD_COMPLETE_PAYMENT_STEP: () => (state) => ({
    ...state,
    step: state.transNo ? STEP.CONFIRMATION : STEP.MAKE_PAYMENT,
  }),
  /** @param {AlgoliaHit} algoliaObject */
  SET_LANDING_PAGE_DATA: (algoliaObject) => (state) => ({
    ...state,
    landingPageData: {
      LandingPageKey: algoliaObject.LandingPageKey,
      LandingPageNameEn: algoliaObject.LandingPageNameEn,
      LandingPageNameFr: algoliaObject.LandingPageNameFr,
      LogoUrl: algoliaObject.LogoUrl,
      LogoAltEn: algoliaObject.LogoAltEn,
      LogoAltFr: algoliaObject.LogoAltFr,
    },
  }),
  /** @param {AlgoliaHit} algoliaObject */
  SELECT_ALGOLIA_OBJECT: (algoliaObject) => (state) => ({
    ...state,
    algoliaObject,
    step: STEP.DETAILS,
  }),
  /**
   * @param {PartnerInfo} partner
   * @param {import('../utils/SmartPaymentLink').SmartPaymentLinkQueryParams|null} prepopulate
   * @param {STEP} [desiredStep]
   * @returns {(state: PaymentProcessState) => PaymentProcessState}
   */
  SET_PARTNER_INFO: (partner, prepopulate, desiredStep = STEP.DETAILS) => (prevState) => {
    let state = prevState;
    let clearState = false;

    // sessionToken from previous payments should've been
    // cleared out if coming from partner search pages
    // if they still exist, user likely pressed the browser back button from
    // /complete-payment, in which case we clear out the context state
    if (prevState.sessionToken) clearState = true;
    // if partner is different from previous,
    // (likely loaded from sessionStorage or using browser back buttons)
    // clear previous states
    if (prevState.partner?.PartnerSeq !== partner.PartnerSeq) clearState = true;

    if (clearState) {
      state = {
        ...initialState,
        algoliaObject: prevState.algoliaObject,
        landingPageData: prevState.landingPageData,
        isSessionLoaded: prevState.isSessionLoaded,
      };
    }

    let { accountDetails, isInvoice, referral, siteMode } = state;

    if (prepopulate) {
      accountDetails ??= {};
      if (prepopulate.email) accountDetails.Email = prepopulate.email;
      if (prepopulate.name) accountDetails.FullName = prepopulate.name;
      if (prepopulate.clientref) accountDetails.ClientRef = prepopulate.clientref;
      if (prepopulate.field1) accountDetails.PARTNERFIELD01 = prepopulate.field1;
      if (prepopulate.field2) accountDetails.PARTNERFIELD02 = prepopulate.field2;
      if (prepopulate.field3) accountDetails.PARTNERFIELD03 = prepopulate.field3;
      if (prepopulate.field4) accountDetails.PARTNERFIELD04 = prepopulate.field4;
      if (prepopulate.field5) accountDetails.PARTNERFIELD05 = prepopulate.field5;
      if (prepopulate.amount) accountDetails.PaymentAmount = prepopulate.amount;
      if (prepopulate.invoiceno) {
        accountDetails.InvoiceNo = prepopulate.invoiceno;
        isInvoice = true;
      }
      if (Object.keys(accountDetails).length === 0) accountDetails = null;

      if (prepopulate.platform) {
        referral ??= {};
        referral.Program = prepopulate.platform;
      }
      if (prepopulate.siteMode) siteMode = prepopulate.siteMode;
      if (prepopulate.referrer) referral = setReferralUrl(referral, prepopulate.referrer);
    }

    let step = desiredStep;
    if (desiredStep === STEP.PAYMENT_OPTION) {
      if (!accountDetails) step = STEP.DETAILS;
    } else if (desiredStep === STEP.REVIEW) {
      if (!accountDetails) step = STEP.DETAILS;
      else if (!state.paymentOption) step = STEP.PAYMENT_OPTION;
    }

    return {
      ...state,
      step,
      partner,
      accountDetails,
      isInvoice,
      referral,
      siteMode,
    };
  },
  /** @param {AccountDetails} accountDetails */
  ENTER_ACCOUNT_DETAILS: (accountDetails) => (state) => ({
    ...state,
    accountDetails,
    step: STEP.PAYMENT_OPTION,
  }),
  /** @param {PaymentOption} paymentOption */
  SELECT_PAYMENT_OPTION: (paymentOption) => (state) => ({
    ...state,
    paymentOption,
    step: STEP.REVIEW,
  }),
  START_OVER: () => (state) => ({
    ...state,
    accountDetails: null,
    paymentOption: null,
  }),
  /**
   * @param {PaymentCode} paymentCode
   * @param {number} totalFee The fee, calculated from the backend
   * @param {number} paymentAmount The payment amount, calculated from the backend
   * @param {number} totalAmount The total amount, calculated from the backend
   * @returns {(state: PaymentProcessState) => PaymentProcessState}
   */
  BEGIN_PAYMENT: (paymentCode, totalFee, paymentAmount, totalAmount, sessionToken) => (state) => ({
    ...state,
    paymentOption: {
      ...state.paymentOption,
      totalFee,
      totalAmount,
    },
    accountDetails: {
      ...state.accountDetails,
      PaymentAmount: paymentAmount.toFixed(2),
    },
    paymentCode,
    sessionToken,
    // stay on the current step for PayPal & PeoplesRFM, otherwise go to the MAKE_PAYMENT step
    step: (state.paymentOption.Name === 'PayPal' || state.paymentOption.Name === 'PeoplesRFM') ? state.step : STEP.MAKE_PAYMENT,
  }),
  COMPLETE_PAYMENT: (transNo) => (state) => ({
    ...state,
    transNo,
    step: STEP.CONFIRMATION,
  }),
  MONERIS_MOBILE: (email, sessionToken) => (state) => ({
    ...state,
    accountDetails: {
      ...state.accountDetails,
      Email: email,
    },
    sessionToken,
  }),
});

/**
 * @callback PaymentProcessEventHandler the action handler to be called by the reducer
 * @param {PaymentProcessState} previousState
 * @returns {PaymentProcessState}
 */
/**
 * @param {PaymentProcessState} state
 * @param {PaymentProcessEventHandler} handler
 * @returns {PaymentProcessState}
 */
const reducer = (state, handler) => handler(state);

/**
 * @param {object} props
 * @param {PaymentProcessEventHandler} props.initializer
 */
export default function PaymentProcessContextProvider({ children, initializer }) {
  const [paymentProcess, dispatchPaymentProcess] = useReducer(reducer, initialState, initializer);

  const contextValue = useMemo(() => (
    { paymentProcess, dispatchPaymentProcess }
  ), [paymentProcess, dispatchPaymentProcess]);

  useEffect(() => {
    // don't overwrite session storage until it's been loaded
    if (!paymentProcess.isSessionLoaded) return;
    sessionStorage.setItem('paymentProcess', serializeState(paymentProcess));
  }, [paymentProcess]);

  useEffect(() => {
    const email = paymentProcess.accountDetails?.Email;
    if (!email) return;
    setAmpUser(email);
    setSentryUser({ email });
  }, [paymentProcess.accountDetails?.Email]);

  return (
    <PaymentProcessContext.Provider value={contextValue}>
      {children}
    </PaymentProcessContext.Provider>
  );
}

export const usePaymentProcessContext = () => (useContext(PaymentProcessContext));

export function useInitStateFromSessionStorage(extraAction, dependency = null) {
  const { dispatchPaymentProcess } = usePaymentProcessContext();

  useEffect(() => {
    const storedState = deserializeState(sessionStorage.getItem('paymentProcess') || '{}');
    dispatchPaymentProcess(DISPATCHERS.LOAD_SESSION(storedState));
    if (extraAction) dispatchPaymentProcess(extraAction);
  }, [dependency]); // eslint-disable-line react-hooks/exhaustive-deps
}
